import React, { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import { useTranslation } from "react-i18next";
import {
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  TablePage,
  type DateRangeValue,
} from "@outpave/ds";
import { Inline } from "components/layout/reusableLayouts";
import ReceiptLibraryGrid from "./ReceiptLibraryGrid";
import EmployeeSearchAutoComplete, {
  Employee,
} from "components/common/EmployeeSearchAutoComplete";
import AmountRangeChip, {
  type AmountRange,
} from "components/common/AmountRangeChip";
import ReceiptConnectDrawer from "./connectReceipt/ReceiptConnectDrawer";
import ReceiptReviewDrawer from "./ReceiptReviewDrawer";
import UploadReceiptDrawer from "./uploadReceipt/UploadReceiptDrawer";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useReceiptPolling } from "hooks/useReceiptPolling";
import { GET_RECEIPTS } from "graphql/receipts";
import {
  Receipt,
  RECEIPT_LIBRARY_FILTERS,
  ReceiptLibraryFilter,
  isReceiptProcessing,
} from "utils/receipts/receiptFormat";
import {
  resolveDateRange,
  useDateRangeFilterLabels,
  useDateRangePresets,
} from "utils/dateRangeFilter";
import {
  DEFAULT_PAGINATION_SIZE,
  MIN_SEARCH_TERM_LENGTH,
} from "components/common/Constants";
import { useAdminTools } from "hooks/useAdminTools";

const EMPTY_AMOUNT_RANGE: AmountRange = {};

export type ReceiptLibraryCriteria = {
  searchTerm: string;
  libraryFilter: ReceiptLibraryFilter;
  amountRange: AmountRange;
  startDate?: number;
  endDate?: number;
  ownerId?: string;
};

export const receiptLibraryVariables = (
  {
    searchTerm,
    libraryFilter,
    amountRange,
    startDate,
    endDate,
    ownerId,
  }: ReceiptLibraryCriteria,
  { mine }: { mine: boolean },
) => {
  const trimmedSearch = searchTerm.trim();
  const { min, max } = amountRange;
  return {
    first: DEFAULT_PAGINATION_SIZE,
    search:
      trimmedSearch.length >= MIN_SEARCH_TERM_LENGTH
        ? trimmedSearch
        : undefined,
    receiptLibraryFilter: libraryFilter,
    amount:
      min !== undefined || max !== undefined
        ? { greater: min, less: max }
        : undefined,
    startDate,
    endDate,
    // The server resolves `mine` to the viewer, so /my never waits on, or trusts, a client-side id.
    ...(mine ? { mine: true } : { userId: ownerId }),
  };
};

type ReceiptLibraryPageProps = {
  mine?: boolean;
};

const ReceiptLibraryPage: React.FC<ReceiptLibraryPageProps> = ({
  mine = false,
}) => {
  const { t } = useTranslation();
  const showOwnerFilter = useAdminTools();
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);
  /** Default Show All, the same combined list as before tabs */
  const [activeTab, setActiveTab] = useState(0);
  const [dateRange, setDateRange] = useState<DateRangeValue>(EMPTY_DATE_RANGE);
  const datePresets = useDateRangePresets();
  const dateLabels = useDateRangeFilterLabels();
  const [amountRange, setAmountRange] =
    useState<AmountRange>(EMPTY_AMOUNT_RANGE);

  const [openDetailDrawer, setOpenDetailDrawer] = useState(false);
  const [openConnectDrawer, setOpenConnectDrawer] = useState(false);
  const [openCreateReceiptDrawer, setOpenCreateReceiptDrawer] = useState(false);

  const [detailReceiptId, setDetailReceiptId] = useState<string>("");
  const [connectReceiptId, setConnectReceiptId] = useState<string>("");
  const [ownerEmployee, setOwnerEmployee] = useState<Employee | null>(null);

  const tabConfigurations = [
    { label: t("Show All") },
    { label: t("Unmatched") },
    { label: t("Scan Failed") },
  ];

  const libraryFilter = RECEIPT_LIBRARY_FILTERS[activeTab];
  const { startDate, endDate } = resolveDateRange(dateRange);
  const initialVariables = useMemo(
    () =>
      receiptLibraryVariables(
        {
          searchTerm,
          libraryFilter,
          amountRange,
          startDate,
          endDate,
          ownerId: ownerEmployee?.id,
        },
        { mine },
      ),
    [
      searchTerm,
      libraryFilter,
      amountRange,
      startDate,
      endDate,
      ownerEmployee?.id,
      mine,
    ],
  );

  const {
    dataArray,
    loading,
    displayLoading,
    isInfiniteScrollFetching,
    refetch,
    handleScroll,
  } = useInfiniteScroll({
    query: GET_RECEIPTS,
    initialVariables,
    extractData: (data) =>
      data.receipts.edges.map((e: { node: Receipt }) => e.node),
  });

  useReceiptPolling(
    refetch,
    dataArray.length,
    loading || isInfiniteScrollFetching,
    (dataArray as Receipt[]).some(isReceiptProcessing),
  );

  const handleOpenDetailDrawer = (receiptId: string) => {
    setDetailReceiptId(receiptId);
    setOpenDetailDrawer(true);
  };

  const handleCloseDetailDrawer = () => {
    setDetailReceiptId("");
    setOpenDetailDrawer(false);
  };

  const handleOpenConnectDrawer = (receiptId: string) => {
    setConnectReceiptId(receiptId);
    setOpenConnectDrawer(true);
  };

  const handleCloseConnectDrawer = () => {
    setConnectReceiptId("");
    setOpenConnectDrawer(false);
  };

  const handleOpenCreateReceiptDrawer = () => {
    setOpenCreateReceiptDrawer(true);
  };

  const handleCloseCreateReceiptDrawer = () => {
    setOpenCreateReceiptDrawer(false);
  };

  return (
    <TablePage
      title={t("Receipt Library")}
      tabs={{
        items: tabConfigurations.map((tab, index) => ({
          id: String(index),
          label: t(tab.label),
        })),
        activeId: String(activeTab),
        onChange: (id) => setActiveTab(Number(id)),
      }}
      search={{
        ...search,
        placeholder: t("Search by merchant or amount..."),
      }}
      filters={
        <Inline gap="var(--space-3)" align="center">
          <DateRangeFilter
            label={t("Date range")}
            presets={datePresets}
            labels={dateLabels}
            value={dateRange}
            onChange={setDateRange}
          />
          <AmountRangeChip
            label={t("Amount")}
            value={amountRange}
            onChange={setAmountRange}
          />
        </Inline>
      }
      primaryAction={
        <Button onClick={() => handleOpenCreateReceiptDrawer()}>
          {t("Upload receipts")}
        </Button>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          height: "100%",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <ReceiptLibraryGrid
            receipts={dataArray as Receipt[]}
            loadingReceipts={displayLoading}
            loadingMore={isInfiniteScrollFetching}
            handleScroll={handleScroll}
            refetchReceipts={refetch}
            handleOpenDetailDrawer={handleOpenDetailDrawer}
            handleOpenConnectDrawer={handleOpenConnectDrawer}
            libraryFilter={libraryFilter}
            renderTopFilter={() => {
              if (showOwnerFilter) {
                return (
                  <Box
                    sx={{
                      flexShrink: 0,
                      maxWidth: 480,
                      paddingBottom: "var(--space-7)",
                    }}
                  >
                    <EmployeeSearchAutoComplete
                      title={t("All users")}
                      employee={ownerEmployee}
                      handleSelectEmployee={setOwnerEmployee}
                    />
                  </Box>
                );
              }
              return null;
            }}
          />
        </Box>
      </Box>
      {openDetailDrawer ? (
        <ReceiptReviewDrawer
          open={openDetailDrawer}
          onClose={handleCloseDetailDrawer}
          receiptId={detailReceiptId}
          onOpenConnectDrawer={() => handleOpenConnectDrawer(detailReceiptId)}
          // The receipt is gone once its last asset is: close the drawer and
          // refetch, the same pair the full-delete path uses.
          onReceiptEmptied={() => {
            handleCloseDetailDrawer();
            refetch();
          }}
        />
      ) : null}
      {openConnectDrawer ? (
        <ReceiptConnectDrawer
          open={openConnectDrawer}
          onClose={handleCloseConnectDrawer}
          receiptId={connectReceiptId}
          mine={mine}
          // A connected receipt leaves the library, so close whatever is open over it.
          onConnected={() => {
            handleCloseConnectDrawer();
            handleCloseDetailDrawer();
            refetch();
          }}
        />
      ) : null}
      <UploadReceiptDrawer
        open={openCreateReceiptDrawer}
        onClose={handleCloseCreateReceiptDrawer}
        // New uploads are still processing, which only Show All lists and polls.
        refetchReceipts={() => (activeTab === 0 ? refetch() : setActiveTab(0))}
      />
    </TablePage>
  );
};

export default ReceiptLibraryPage;
